import React from "react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { Button } from '../../../ui/Button';
import { Overlay } from '../../../ui/Overlay';

interface ClientSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  value: string; // already has the tenant's Client ID substituted in, when known
  tenantId: string;
  tenantName: string;
}

const ClientSelectModal: React.FC<ClientSelectModalProps> = ({
  isOpen,
  onClose,
  title,
  value,
  tenantId,
  tenantName,
}) => {
  if (!isOpen) return null;

  const copy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        toast.success('Copied to clipboard!');
        return;
      }
      // Fallback for older browsers without the Clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          toast.success('Copied to clipboard!');
        } else {
          throw new Error('Copy command failed');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Overlay tone="dim-40" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 relative">
        <Button tone="custom"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>

        <h3 className="text-lg font-semibold mb-4">{title}</h3>

        {/* Tenant Context */}
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Logged-in tenant</p>
          {tenantId ? (
            <p className="mt-1 text-sm text-gray-800">
              {tenantName || 'Tenant'} <span className="text-gray-500">({tenantId})</span>
            </p>
          ) : (
            <p className="mt-1 text-sm text-amber-700">
              No tenant ID is available in the current session.
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            {tenantId
              ? 'Your Client ID has been filled into the code below automatically.'
              : <>The code below still uses the <code className="font-mono">CLIENT_ID</code> placeholder. Replace it before embedding.</>}
          </p>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 border rounded-lg p-3 mb-4">
          <pre className="text-xs whitespace-pre-wrap break-all">
            {value}
          </pre>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button tone="custom"
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg"
          >
            Cancel
          </Button>
          <Button tone="blue-static"
            onClick={copy}
            className="px-4 py-2 text-sm rounded-lg flex items-center gap-1"
          >
            <CheckIcon className="h-4 w-4" />
            Copy
          </Button>
        </div>
      </div>
    </Overlay>
  );
};

export default ClientSelectModal;
